import moment from 'moment';
const defaultUtc = 'YYYY-MM-DD HH:mm Z';
const defaultUtcWithSeconds = 'YYYY-MM-DD HH:mm:ss Z';
const dateTimeQuery = 'YYYY-MM-DD HH:mm';
const dateFormat = 'DDMMMYY HH:mm';
const dateFormatWithoutTime = 'DDMMMYY';
const shortDateFormat = 'DD/MMM/YYYY';
const DateTimeFormat = {
  toFullDate(date: any) {
    if (moment(date, defaultUtc).isValid()) return moment(date, defaultUtc).format(defaultUtc);

    return date;
  },
  // Endpoints hand us UTC timestamps, and some of them carry no `Z` or offset. Those
  // have to be parsed as UTC and then converted, which is what `moment.utc(..).local()`
  // does -- `moment(..)` would read a naive timestamp as if it were already local time,
  // so Local and UTC rendered the exact same digits. Timestamps that do carry an offset
  // are unaffected: they parse to the same instant either way.
  toStandardDate(date: any, isUtc = true, withoutTime = false) {
    if (moment(date, defaultUtc).isValid()) {
      const parsed = moment.utc(date, defaultUtc);

      return (isUtc ? parsed : parsed.local()).format(withoutTime ? dateFormatWithoutTime : dateFormat);
    }

    return date;
  },
  toStandardDateWithSeconds(date: any, isUtc = true, withoutTime = false) {
    if (moment(date, moment.ISO_8601, true).isValid()) {
      const parsed = moment.utc(date);

      return (isUtc ? parsed : parsed.local()).format(withoutTime ? dateFormatWithoutTime : 'DDMMMYY HH:mm:ss');
    }

    return date;
  },
  toTimeFromNow(date: any, isUtc = true) {
    if (moment(date, defaultUtc).isValid()) {
      const parsed = moment.utc(date, defaultUtc);

      return (isUtc ? parsed : parsed.local()).fromNow();
    }

    return date;
  },
  timeFromNow() {
    return moment().fromNow();
  },
  toQuery(date: any) {
    if (moment(date, dateTimeQuery).isValid()) return moment(date).format(dateTimeQuery).toString();

    return date;
  },
};

const NumberFormat = {
  toCurrency(number: number, currency: string) {
    if (!currency) currency = '$';
    return `${currency} ${number}`;
  },
  toFixedPoint(num: number, p: number) {
    if (num) return num.toFixed(p);

    return `${num}`;
  },
};

const StringFormat = {
  capitalize(s: any) {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  },
};

const secondsToDhms = (seconds) => {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  // const remainingSeconds = seconds % 60;
  const result: any = [];
  if (days > 0) result.push(`${days}d`);
  if (hours > 0) result.push(`${hours}hr`);
  if (minutes > 0) result.push(`${minutes}min`);
  // if (remainingSeconds > 0) result.push(`${remainingSeconds}sec`);
  return result.join(' ');
};

/**
 * Human-readable duration from a count of seconds (fractional seconds are fine,
 * e.g. 12524.166666666666 -> "3h 28m").
 *
 * Shows the two largest non-zero units so the value stays scannable in a table
 * cell, and never collapses to an empty string the way `secondsToDhms` does for
 * sub-minute values. Returns '' for missing/invalid input so callers can pick
 * their own placeholder.
 */
const formatDurationFromSeconds = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === '') return '';

  const totalSeconds = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '';

  const rounded = Math.round(totalSeconds);
  if (rounded === 0) return '0s';

  const days = Math.floor(rounded / 86400);
  const hours = Math.floor((rounded % 86400) / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.slice(0, 2).join(' ');
};

const FormatForApi = (date: moment.Moment, startOfDay = true): string => {
  return startOfDay ? date.startOf('day').format('YYYY-MM-DD HH:mm') : date.endOf('day').format('YYYY-MM-DD HH:mm');
};

export {
  DateTimeFormat,
  NumberFormat,
  dateFormat,
  shortDateFormat,
  dateFormatWithoutTime,
  StringFormat,
  defaultUtcWithSeconds,
  secondsToDhms,
  formatDurationFromSeconds,
  FormatForApi,
};
